import React, { useState, useRef } from 'react';
import axios from 'axios';
import './GarmentAnalyzer.css';

const API_URL = 'https://api.hauteuar.com/garment-api'; // Update this to match your Flask API URL

const GarmentAnalyzer = () => {
  const [selectedFile, setSelectedFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [result, setResult] = useState(null);
  const fileInputRef = useRef(null);
  const resultContainerRef = useRef(null);

  // Handle file selection
  const handleFileChange = (event) => {
    const file = event.target.files[0];
    if (file) {
      setSelectedFile(file);
      setError(null);
      
      // Create preview URL
      const reader = new FileReader();
      reader.onload = () => {
        setPreviewUrl(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  // Handle file drop
  const handleDrop = (event) => {
    event.preventDefault();
    const file = event.dataTransfer.files[0];
    if (file && file.type.startsWith('image/')) {
      setSelectedFile(file);
      setError(null);
      
      // Create preview URL
      const reader = new FileReader();
      reader.onload = () => {
        setPreviewUrl(reader.result);
      };
      reader.readAsDataURL(file);
    } else {
      setError('Please drop an image file');
    }
  };

  // Prevent default behavior for drag over events
  const handleDragOver = (event) => {
    event.preventDefault();
  };

  // Handle analyze button click
  const handleAnalyze = async () => {
    if (!selectedFile) {
      setError('Please select an image first');
      return;
    }

    setLoading(true);
    setError(null);
    setResult(null);

    try {
      // Convert image to base64
      const reader = new FileReader();
      reader.readAsDataURL(selectedFile);
      
      reader.onload = async () => {
        const base64Image = reader.result.split(',')[1]; // Remove data URL prefix
        
        try {
          // Call API
          const response = await axios.post(`${API_URL}/analyze-garment`, {
            image: base64Image
          });
          
          // Set result
          setResult(response.data);
          
          // Scroll to result after a short delay to ensure it's rendered
          setTimeout(() => {
            if (resultContainerRef.current) {
              resultContainerRef.current.scrollIntoView({ behavior: 'smooth' });
            }
          }, 100);
          
          // Set loading to false after successful response
          setLoading(false);
        } catch (apiError) {
          setError('Error analyzing garment: ' + (apiError.response?.data?.error || apiError.message));
          setLoading(false);
        }
      };
      
      reader.onerror = (readerError) => {
        setError('Error reading file: ' + readerError);
        setLoading(false);
      };
    } catch (err) {
      setError('Error analyzing garment: ' + (err.response?.data?.error || err.message));
      setLoading(false);
    }
  };

  // Reset form
  const handleReset = () => {
    setSelectedFile(null);
    setPreviewUrl(null);
    setError(null);
    setResult(null);
    setLoading(false); // Also reset loading state on form reset
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  return (
    <div className="garment-analyzer">
      <h1>Garment Analysis Tool</h1>
      
      <div className="upload-section">
        <div 
          className="drop-zone" 
          onDrop={handleDrop} 
          onDragOver={handleDragOver}
        >
          {previewUrl ? (
            <div className="preview-container">
              <img src={previewUrl} alt="Preview" className="image-preview" />
              <button className="remove-btn" onClick={handleReset}>×</button>
            </div>
          ) : (
            <>
              <p>Drag & drop your garment image here</p>
              <p>or</p>
              <input 
                type="file" 
                accept="image/*" 
                onChange={handleFileChange} 
                ref={fileInputRef}
                className="file-input"
              />
              <button 
                className="browse-btn"
                onClick={() => fileInputRef.current.click()}
              >
                Browse Files
              </button>
            </>
          )}
        </div>
        
        {error && <div className="error-message">{error}</div>}
        
        <div className="button-group">
          <button 
            className="analyze-btn"
            onClick={handleAnalyze}
            disabled={!selectedFile || loading}
          >
            {loading ? 'Analyzing...' : 'Analyze Garment'}
          </button>
          
          {selectedFile && (
            <button 
              className="reset-btn"
              onClick={handleReset}
              disabled={loading}
            >
              Reset
            </button>
          )}
        </div>
      </div>
      
      {loading && (
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Analyzing your garment. This may take a minute...</p>
        </div>
      )}
      
      {result && (
        <div className="result-container" ref={resultContainerRef}>
          <h2>Analysis Results</h2>
          
          {result.key_insights && (
            <div className="key-insights">
              <h3>Key Insights</h3>
              <div className="insights-grid">
                <div className="insight-item">
                  <span className="insight-label">Garment Type:</span>
                  <span className="insight-value">{result.key_insights.garment_type}</span>
                </div>
                
                {result.key_insights.color_scheme && result.key_insights.color_scheme.length > 0 && (
                  <div className="insight-item">
                    <span className="insight-label">Colors:</span>
                    <span className="insight-value">{result.key_insights.color_scheme.join(', ')}</span>
                  </div>
                )}
                
                {result.key_insights.fabric_summary && (
                  <div className="insight-item">
                    <span className="insight-label">Fabric:</span>
                    <span className="insight-value">{result.key_insights.fabric_summary}</span>
                  </div>
                )}
              </div>
            </div>
          )}
          
          <div className="html-report">
            <iframe
              title="Garment Analysis Report"
              srcDoc={result.html_report}
              width="100%"
              height="800px"
              frameBorder="0"
            ></iframe>
          </div>
        </div>
      )}
    </div>
  );
};

export default GarmentAnalyzer;